import type { Socket } from "socket.io-client";

type Store = {
  set: (path: string, value: unknown, opts?: { source?: string }) => void;
  batch: (fn: () => void) => void;
  get: <T = unknown>(path: string) => T | undefined;
};

type ScreenStatePathFn = (projectId: string, key: string) => string;

export const leaveProjectRoom = (socket: Socket, projectId: string): void => {
  try {
    socket.emit("project:leave", { projectId });
  } catch {}
};

export const joinProjectRoom = (socket: Socket, projectId: string): void => {
  try {
    socket.emit("project:join", { projectId }, () => {});
  } catch {}
  // Optional legacy join for debugging/compat
  try {
    const room = `project:${projectId}`;
    socket.emit("join", room, () => {});
  } catch {}
};

/**
 * Registers the project:prefs:delta listener with normalization and writes to store.
 * Returns a cleanup function to remove listeners that this function attached.
 */
export const wireProjectPrefsDelta = (
  socket: Socket,
  deps: {
    store: Store;
    screenStatePath: ScreenStatePathFn;
    sourceId: string;
    getCurrentProjectId: () => string | null;
    normalize?: (
      patch: Record<string, unknown>,
      projectId: string
    ) => { path: string; value: unknown }[];
  }
): (() => void) => {
  const { store, screenStatePath, sourceId, getCurrentProjectId, normalize } =
    deps;

  const handler = (payload: {
    patches?: { path: string; value: unknown }[];
    sourceId?: string;
    projectId?: string;
    patch?: Record<string, unknown>;
  }) => {
    try {
      if (!payload) return;
      if (payload.sourceId && payload.sourceId === sourceId) {
        return;
      }

      let patches: { path: string; value: unknown }[] | null = null;
      if (Array.isArray((payload as any).patches)) {
        patches = (payload as any).patches;
      } else if (payload.patch && payload.projectId) {
        const pid = payload.projectId;
        // Try adapter-provided normalization first
        if (normalize) {
          try {
            const np = normalize(payload.patch as any, pid!);
            if (Array.isArray(np) && np.length) {
              patches = np;
            }
          } catch {}
        }
        if (patches) {
          // already normalized by adapters
        } else {
          const p = payload.patch as Record<string, unknown>;
          const keys = Object.keys(p);

          const hasTx = Object.prototype.hasOwnProperty.call(p, "translateX");
          const hasTy = Object.prototype.hasOwnProperty.call(p, "translateY");
          const nextPatches: { path: string; value: unknown }[] = [];

          const mapKey = (key: string): string => {
            const k = key as string;
            if (k === "zoom" || k === "zoom_value") return "zoomValue";
            if (k === "stage_position" || k === "position")
              return "stagePosition";
            if (k === "breakpoint" || k === "current_breakpoint")
              return "currentBreakpoint";
            if (k === "language" || k === "current_language")
              return "currentLanguage";
            if (k === "selectedSubtool" || k === "selected_subtool")
              return "selectedTool";
            if (k === "sketch_eraser_mode") return "sketchEraserMode";
            if (k === "sketch_selection_mode") return "sketchSelectionMode";
            return k;
          };

          for (const k of keys) {
            if (k === "translateX" || k === "translateY") continue;
            const mapped = mapKey(k);
            nextPatches.push({
              path: screenStatePath(pid!, mapped),
              value: (p as any)[k],
            });
          }

          if (hasTx || hasTy) {
            try {
              const current = (store.get<any>(
                screenStatePath(pid!, "stagePosition")
              ) as any) || {
                x: 0,
                y: 0,
              };
              const nx = hasTx ? (p as any).translateX : current.x;
              const ny = hasTy ? (p as any).translateY : current.y;
              nextPatches.push({
                path: screenStatePath(pid!, "stagePosition"),
                value: { x: nx, y: ny },
              });
            } catch {}
          }
          patches = nextPatches;
        }
      }
      if (!patches || !patches.length) return;

      // Ensure only applies to current project
      const pid = payload.projectId;
      const currentPid = getCurrentProjectId();
      if (pid && currentPid && pid !== currentPid) return;

      store.batch(() => {
        for (const p of patches!) {
          try {
            store.set(p.path, p.value as any, { source: "socket" });
          } catch {}
        }
      });
    } catch {}
  };

  try {
    socket.off("project:prefs:delta");
  } catch {}
  socket.on("project:prefs:delta", handler as any);

  // Optional debug listener
  return () => {
    try {
      socket.off("project:prefs:delta", handler as any);
    } catch {}
  };
};
